<template>
  <div class="detail-score-panel" :class="{ 'is-readonly': readonly }">
    <div class="subject-nav">
      <button
        v-for="subject in subjects"
        :key="subject.key"
        type="button"
        class="subject-nav-item"
        :class="{ active: activeKey === subject.key }"
        @click="activeKey = subject.key"
      >
        <span>{{ subject.name }}</span>
        <strong>{{ subjectTotal(subject.key) }}分</strong>
      </button>
    </div>
    <div class="subject-content">
      <div class="subject-header">
        <div>
          <div class="subject-name">{{ activeSubject.name }}</div>
          <div class="subject-count">共 {{ activeSubject.count }} 题</div>
        </div>
        <div class="subject-total">合计 <strong>{{ subjectTotal(activeKey) }}</strong> 分</div>
      </div>
      <div class="question-grid" :class="{ 'is-nested': isNestedSubject(activeKey) }">
        <template v-for="group in questionColumns">
          <div v-if="group.name" :key="`${group.name}-header`" class="digit-span-group digit-span-group-header">
            {{ group.name }}
          </div>
          <div
            v-for="(column, columnIndex) in group.columns"
            :key="`${group.name}-col-${columnIndex}`"
            class="question-column"
          >
            <div
              v-for="index in column"
              :key="index"
              class="question-item"
              :class="{ 'has-subquestions': isNestedSubject(activeKey) }"
            >
            <template v-if="isNestedSubject(activeKey)">
              <div class="question-title-row">
                <span class="question-number">第 {{ displayQuestionNumber(activeKey, index) }} 题</span>
                <span class="question-score">本题 <strong>{{ questionValue(activeKey, index - 1) }}</strong> 分</span>
              </div>
              <div class="subquestion-list" :class="`columns-${subquestionCount(activeKey)}`">
                <div v-for="subIndex in subquestionCount(activeKey)" :key="subIndex" class="subquestion-item">
                  <span>小题 {{ subIndex }}</span>
                  <span v-if="readonly" class="subquestion-readonly">
                    {{ subquestionValue(activeKey, index - 1, subIndex - 1) }}
                  </span>
                  <a-radio-group
                    v-else
                    :value="subquestionValue(activeKey, index - 1, subIndex - 1)"
                    button-style="solid"
                    size="small"
                    @change="changeSubquestionScore(index - 1, subIndex - 1, $event.target.value)"
                  >
                    <a-radio-button :value="0">0</a-radio-button>
                    <a-radio-button :value="1">1</a-radio-button>
                  </a-radio-group>
                </div>
              </div>
            </template>
            <template v-else>
              <span class="question-number">第 {{ index }} 题</span>
              <span v-if="readonly" class="readonly-score">{{ questionValue(activeKey, index - 1) }}</span>
              <a-radio-group
                v-else
                :value="questionValue(activeKey, index - 1)"
                button-style="solid"
                size="small"
                @change="changeScore(index - 1, $event.target.value)"
              >
                <a-radio-button
                  v-for="option in scoreOptions(activeKey, index - 1)"
                  :key="option"
                  :value="option"
                >
                  {{ option }}
                </a-radio-button>
              </a-radio-group>
            </template>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
const COMPLETE_SUBJECTS = [
  { key: 'bd', name: '积木', count: 14 },
  { key: 'si', name: '类同', count: 23 },
  { key: 'dsf', name: '顺序背数', count: 10 },
  { key: 'dsb', name: '倒序背数', count: 9 },
  { key: 'pc', name: '图画概念', count: 28 },
  { key: 'vc', name: '词汇', count: 36 },
  { key: 'ln', name: '字母-数字', count: 10 },
  { key: 'mr', name: '矩阵推理', count: 35 },
  { key: 'co', name: '理解', count: 21 },
  { key: 'pcm', name: '填图', count: 38 },
  { key: 'in', name: '常识', count: 33 },
  { key: 'ar', name: '算术', count: 34 },
];

const SIMPLE_SUBJECTS = [
  { key: 'bd', name: '积木', count: 14 },
  { key: 'si', name: '类同', count: 23 },
  { key: 'ds', name: '背数', count: 19 },
  { key: 'mr', name: '矩阵推理', count: 35 },
  { key: 'co', name: '理解', count: 21 },
];

const COMPLETE_SUBQUESTION_COUNTS = {
  dsf: 2,
  dsb: 2,
  ln: 3,
};

const SIMPLE_SUBQUESTION_COUNTS = {
  ds: 2,
};

export default {
  name: 'WiscScoreDetail',
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    edition: {
      type: [String, Number],
      default: '4',
    },
    simpleSubTestNum: {
      type: [String, Number],
      default: 6,
    },
  },
  data() {
    return {
      activeKey: 'bd',
    };
  },
  computed: {
    isSimple() {
      return String(this.edition) === '5';
    },
    isSimpleDigitSpan() {
      return this.isSimple && this.activeKey === 'ds';
    },
    subjects() {
      if (!this.isSimple) return COMPLETE_SUBJECTS;
      const subjectKeys = {
        4: ['si', 'ds', 'mr'],
        5: ['bd', 'si', 'mr', 'co'],
        6: ['bd', 'si', 'ds', 'mr', 'co'],
      }[Number(this.simpleSubTestNum) || 6];
      return SIMPLE_SUBJECTS.filter((subject) => subjectKeys.includes(subject.key));
    },
    subquestionCounts() {
      return this.isSimple ? SIMPLE_SUBQUESTION_COUNTS : COMPLETE_SUBQUESTION_COUNTS;
    },
    activeSubject() {
      return this.subjects.find((subject) => subject.key === this.activeKey) || this.subjects[0];
    },
    questionGroups() {
      if (!this.isSimpleDigitSpan) {
        return [{ name: '', indexes: Array.from({ length: this.activeSubject.count }, (_, index) => index + 1) }];
      }
      return [
        { name: '顺序背数（第 1-10 题）', indexes: Array.from({ length: 10 }, (_, index) => index + 1) },
        { name: '倒序背数（第 11-19 题）', indexes: Array.from({ length: 9 }, (_, index) => index + 11) },
      ];
    },
    questionColumns() {
      return this.questionGroups.map((group) => {
        const half = Math.ceil(group.indexes.length / 2);
        return {
          name: group.name,
          columns: [group.indexes.slice(0, half), group.indexes.slice(half)],
        };
      });
    },
  },
  watch: {
    simpleSubTestNum() {
      if (!this.isSimple) return;
      this.activeKey = this.subjects[0].key;
      if (!this.readonly) this.$emit('input', this.normalizedScores());
    },
    value: {
      handler() {
        this.$emit('totals-change', this.calculateTotals(this.normalizedScores()));
      },
      immediate: true,
      deep: true,
    },
  },
  mounted() {
    if (!this.subjects.some((subject) => subject.key === this.activeKey)) {
      this.activeKey = this.subjects[0].key;
    }
    if (!this.readonly) {
      this.$emit('input', this.normalizedScores());
    }
  },
  methods: {
    normalizedScores() {
      const normalized = {};
      this.subjects.forEach((subject) => {
        const values = Array.isArray(this.value && this.value[subject.key]) ? this.value[subject.key] : [];
        normalized[subject.key] = Array.from({ length: subject.count }, (_, index) => {
          if (this.isNestedSubject(subject.key)) {
            const rawValue = values[index];
            const legacyScore = Array.isArray(rawValue) ? 0 : Math.max(0, Math.min(Number(rawValue) || 0, this.subquestionCount(subject.key)));
            return Array.from({ length: this.subquestionCount(subject.key) }, (__, subIndex) => {
              if (Array.isArray(rawValue)) {
                return Number(rawValue[subIndex]) === 1 ? 1 : 0;
              }
              return subIndex < legacyScore ? 1 : 0;
            });
          }
          const value = Number(values[index]);
          return this.scoreOptions(subject.key, index).includes(value) ? value : 0;
        });
      });
      return normalized;
    },
    questionValue(subject, index) {
      return this.scoreTotal(this.normalizedScores()[subject][index]);
    },
    displayQuestionNumber(subject, index) {
      return this.isSimple && subject === 'ds' && index > 10 ? index - 10 : index;
    },
    subquestionValue(subject, questionIndex, subquestionIndex) {
      return this.normalizedScores()[subject][questionIndex][subquestionIndex];
    },
    changeScore(index, value) {
      const scores = this.normalizedScores();
      this.$set(scores[this.activeKey], index, Number(value));
      this.$emit('input', scores);
      this.$emit('totals-change', this.calculateTotals(scores));
    },
    changeSubquestionScore(questionIndex, subquestionIndex, value) {
      const scores = this.normalizedScores();
      this.$set(scores[this.activeKey][questionIndex], subquestionIndex, Number(value));
      this.$emit('input', scores);
      this.$emit('totals-change', this.calculateTotals(scores));
    },
    subjectTotal(subject) {
      return this.normalizedScores()[subject].reduce((total, value) => total + this.scoreTotal(value), 0);
    },
    calculateTotals(scores) {
      const sum = (key) => (scores[key] || []).reduce((total, value) => total + this.scoreTotal(value), 0);
      if (this.isSimple) {
        return this.subjects.reduce((totals, subject) => {
          totals[`score_${subject.key}`] = sum(subject.key);
          return totals;
        }, {});
      }
      const scoreDsf = sum('dsf');
      const scoreDsb = sum('dsb');
      return {
        score_bd: sum('bd'),
        score_bdn: scores.bd.reduce((total, value) => total + Math.min(value, 4), 0),
        score_si: sum('si'),
        score_dsf: scoreDsf,
        score_dsb: scoreDsb,
        score_ds: scoreDsf + scoreDsb,
        score_pc: sum('pc'),
        score_vc: sum('vc'),
        score_ln: sum('ln'),
        score_mr: sum('mr'),
        score_co: sum('co'),
        score_pcm: sum('pcm'),
        score_in: sum('in'),
        score_ar: sum('ar'),
      };
    },
    scoreOptions(subject, index) {
      if (subject === 'bd') {
        if (index < 3) return [0, 1, 2];
        if (index < 8) return [0, 4];
        return [0, 4, 5, 6, 7];
      }
      if (subject === 'si') return index < 2 ? [0, 1] : [0, 1, 2];
      if (subject === 'vc') return index < 4 ? [0, 1] : [0, 1, 2];
      if (subject === 'co') return [0, 1, 2];
      return [0, 1];
    },
    isNestedSubject(subject) {
      return Boolean(this.subquestionCounts[subject]);
    },
    subquestionCount(subject) {
      return this.subquestionCounts[subject] || 0;
    },
    scoreTotal(value) {
      return Array.isArray(value) ? value.reduce((total, score) => total + Number(score || 0), 0) : Number(value || 0);
    },
  },
};
</script>

<style scoped>
.detail-score-panel {
  display: grid;
  grid-template-columns: 180px minmax(0, 1fr);
  min-height: 360px;
  margin: 24px 0 34px;
  border: 1px solid #eadfea;
  border-radius: 6px;
  background: #fff;
  overflow: hidden;
}

.subject-nav {
  padding: 10px;
  background: #faf7fa;
  border-right: 1px solid #eadfea;
}

.subject-nav-item {
  width: 100%;
  min-height: 42px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 10px;
  border: 0;
  border-radius: 4px;
  color: #555;
  background: transparent;
  cursor: pointer;
}

.subject-nav-item strong {
  font-size: 12px;
  color: #999;
}

.subject-nav-item.active {
  color: #832c75;
  background: #f1e7f0;
  font-weight: 600;
}

.subject-nav-item.active strong {
  color: #832c75;
}

.subject-content {
  min-width: 0;
  padding: 20px 22px 24px;
}

.subject-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 16px;
  border-bottom: 1px solid #eee;
}

.subject-name {
  color: #333;
  font-size: 16px;
  font-weight: 600;
}

.subject-count {
  margin-top: 3px;
  color: #999;
  font-size: 12px;
}

.subject-total {
  color: #666;
}

.subject-total strong {
  color: #832c75;
  font-size: 20px;
}

.question-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(260px, 1fr));
  gap: 10px 18px;
  padding-top: 18px;
}

.question-column {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.detail-score-panel.is-readonly .question-grid {
  grid-template-columns: repeat(3, minmax(180px, 1fr));
}

.detail-score-panel.is-readonly .question-column {
  display: contents;
}

.question-item {
  min-height: 38px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 8px 4px 12px;
  border-bottom: 1px solid #f1f1f1;
}

.question-grid.is-nested {
  grid-template-columns: repeat(2, minmax(300px, 1fr));
}

.detail-score-panel.is-readonly .question-grid.is-nested {
  grid-template-columns: repeat(2, minmax(260px, 1fr));
}

.question-item.has-subquestions {
  min-height: 108px;
  display: block;
  padding: 12px;
  border: 1px solid #eadfea;
  border-radius: 5px;
  background: #fcfafc;
}

.question-title-row,
.subquestion-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.question-title-row {
  padding-bottom: 9px;
  border-bottom: 1px solid #eee5ed;
}

.digit-span-group {
  display: inline-flex;
  margin-bottom: 8px;
  padding: 2px 8px;
  border-left: 3px solid #832c75;
  color: #6d2f67;
  background: #f4ebf3;
  font-size: 12px;
  font-weight: 600;
}

.digit-span-group-header {
  grid-column: 1 / -1;
  margin-bottom: -2px;
}

.question-score {
  color: #888;
  font-size: 12px;
}

.question-score strong {
  color: #832c75;
  font-size: 16px;
}

.subquestion-list {
  display: grid;
  gap: 0;
  margin-top: 10px;
  overflow: hidden;
  border: 1px solid #e4d7e2;
  border-radius: 4px;
  background: #fff;
}

.subquestion-list.columns-2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.subquestion-list.columns-3 {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.subquestion-item {
  min-width: 0;
  gap: 8px;
  padding: 8px 10px;
}

.subquestion-item + .subquestion-item {
  border-left: 1px solid #e4d7e2;
}

.subquestion-item > span:first-child {
  color: #777;
  font-size: 12px;
  white-space: nowrap;
}

.subquestion-readonly {
  display: grid;
  width: 30px;
  height: 26px;
  place-items: center;
  border-radius: 4px;
  color: #832c75;
  background: #f1e7f0;
  font-weight: 600;
}

.question-number {
  flex: 0 0 62px;
  color: #666;
  font-size: 13px;
}

.readonly-score {
  width: 34px;
  height: 28px;
  line-height: 28px;
  text-align: center;
  color: #832c75;
  background: #f1e7f0;
  border-radius: 4px;
  font-weight: 600;
}

@media (max-width: 900px) {
  .detail-score-panel {
    grid-template-columns: 1fr;
  }

  .subject-nav {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    border-right: 0;
    border-bottom: 1px solid #eadfea;
  }

  .question-grid {
    grid-template-columns: 1fr;
  }

  .question-grid.is-nested {
    grid-template-columns: 1fr;
  }

  .detail-score-panel.is-readonly .question-grid {
    grid-template-columns: repeat(2, minmax(180px, 1fr));
  }
}

@media (max-width: 560px) {
  .subject-nav {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .detail-score-panel.is-readonly .question-grid {
    grid-template-columns: 1fr;
  }

  .subquestion-list.columns-2,
  .subquestion-list.columns-3 {
    grid-template-columns: 1fr;
  }

  .subquestion-item + .subquestion-item {
    border-top: 1px solid #e4d7e2;
    border-left: 0;
  }
}
</style>
